import type {
  ConfigObjectString,
  MindstringPrototype,
  SituationConfigOutput,
  SituationConfigPatch,
  SupportedLanguage,
} from "@/generated/fastapi";

export type SituationConfig = SituationConfigOutput;

export type SituationConfigTouchedField =
  | "show_position"
  | "mask_position"
  | "override_position"
  | "position_snippet"
  | "show_context"
  | "mask_context"
  | "override_context"
  | "show_location"
  | "override_location"
  | "show_date_from"
  | "show_date_to";

export type ResolvedSituationOutputConfig = {
  show_position: boolean;
  override_position: ConfigObjectString;
  mask_position: string;
  position_snippet: ConfigObjectString;
  show_context: boolean;
  override_context: ConfigObjectString;
  mask_context: ConfigObjectString;
  show_location: boolean;
  override_location: ConfigObjectString;
  show_date_from: boolean;
  show_date_to: boolean;
};

const EMPTY_CONFIG_OBJECT: ConfigObjectString = {
  text: null,
  enabled: false,
};

function cloneConfig(config: SituationConfig): SituationConfig {
  return JSON.parse(JSON.stringify(config)) as SituationConfig;
}

function configObject(value?: ConfigObjectString): ConfigObjectString {
  return {
    ...EMPTY_CONFIG_OBJECT,
    ...value,
  };
}

export function getResolvedSituationOutputConfig(
  config: SituationConfig,
  language: SupportedLanguage,
  prototype: MindstringPrototype,
): ResolvedSituationOutputConfig {
  return {
    show_position: config.show_position,
    override_position: configObject(config.override_position[language]),
    mask_position: config.mask_position?.[language] ?? "",
    position_snippet: configObject(config.position_snippet[language]?.[prototype]),
    show_context: config.show_context[prototype] ?? false,
    override_context: configObject(config.override_context?.[language]?.[prototype]),
    mask_context: configObject(config.mask_context?.[language]?.[prototype]),
    show_location: config.show_location,
    override_location: configObject(config.override_location[language]),
    show_date_from: config.show_date_from[prototype] ?? false,
    show_date_to: config.show_date_to[prototype] ?? false,
  };
}

export function setShowPosition(
  config: SituationConfig,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    show_position: value,
  };
}

export function setMaskPositionText(
  config: SituationConfig,
  language: SupportedLanguage,
  value: string,
): SituationConfig {
  return {
    ...config,
    mask_position: {
      ...config.mask_position,
      [language]: value,
    },
  };
}

export function setOverridePositionEnabled(
  config: SituationConfig,
  language: SupportedLanguage,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    override_position: {
      ...config.override_position,
      [language]: {
        ...configObject(config.override_position[language]),
        enabled: value,
      },
    },
  };
}

export function setOverridePositionText(
  config: SituationConfig,
  language: SupportedLanguage,
  value: string,
): SituationConfig {
  return {
    ...config,
    override_position: {
      ...config.override_position,
      [language]: {
        ...configObject(config.override_position[language]),
        text: value || null,
      },
    },
  };
}

export function setPositionSnippetEnabled(
  config: SituationConfig,
  language: SupportedLanguage,
  prototype: MindstringPrototype,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    position_snippet: {
      ...config.position_snippet,
      [language]: {
        ...config.position_snippet[language],
        [prototype]: {
          ...configObject(config.position_snippet[language]?.[prototype]),
          enabled: value,
        },
      },
    },
  };
}

export function setPositionSnippetText(
  config: SituationConfig,
  language: SupportedLanguage,
  prototype: MindstringPrototype,
  value: string,
): SituationConfig {
  return {
    ...config,
    position_snippet: {
      ...config.position_snippet,
      [language]: {
        ...config.position_snippet[language],
        [prototype]: {
          ...configObject(config.position_snippet[language]?.[prototype]),
          text: value || null,
        },
      },
    },
  };
}

export function setShowContext(
  config: SituationConfig,
  prototype: MindstringPrototype,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    show_context: {
      ...config.show_context,
      [prototype]: value,
    },
  };
}

export function setMaskContextEnabled(
  config: SituationConfig,
  language: SupportedLanguage,
  prototype: MindstringPrototype,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    mask_context: {
      ...config.mask_context,
      [language]: {
        ...config.mask_context[language],
        [prototype]: {
          ...configObject(config.mask_context[language]?.[prototype]),
          enabled: value,
        },
      },
    },
  };
}

export function setMaskContextText(
  config: SituationConfig,
  language: SupportedLanguage,
  prototype: MindstringPrototype,
  value: string,
): SituationConfig {
  return {
    ...config,
    mask_context: {
      ...config.mask_context,
      [language]: {
        ...config.mask_context[language],
        [prototype]: {
          ...configObject(config.mask_context[language]?.[prototype]),
          text: value || null,
        },
      },
    },
  };
}

export function setOverrideContextEnabled(
  config: SituationConfig,
  language: SupportedLanguage,
  prototype: MindstringPrototype,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    override_context: {
      ...config.override_context,
      [language]: {
        ...config.override_context[language],
        [prototype]: {
          ...configObject(config.override_context[language]?.[prototype]),
          enabled: value,
        },
      },
    },
  };
}

export function setOverrideContextText(
  config: SituationConfig,
  language: SupportedLanguage,
  prototype: MindstringPrototype,
  value: string,
): SituationConfig {
  return {
    ...config,
    override_context: {
      ...config.override_context,
      [language]: {
        ...config.override_context[language],
        [prototype]: {
          ...configObject(config.override_context[language]?.[prototype]),
          text: value || null,
        },
      },
    },
  };
}

export function setShowLocation(
  config: SituationConfig,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    show_location: value,
  };
}

export function setOverrideLocationEnabled(
  config: SituationConfig,
  language: SupportedLanguage,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    override_location: {
      ...config.override_location,
      [language]: {
        ...configObject(config.override_location[language]),
        enabled: value,
      },
    },
  };
}

export function setOverrideLocationText(
  config: SituationConfig,
  language: SupportedLanguage,
  value: string,
): SituationConfig {
  return {
    ...config,
    override_location: {
      ...config.override_location,
      [language]: {
        ...configObject(config.override_location[language]),
        text: value || null,
      },
    },
  };
}

export function setShowDateFrom(
  config: SituationConfig,
  prototype: MindstringPrototype,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    show_date_from: {
      ...config.show_date_from,
      [prototype]: value,
    },
  };
}

export function setShowDateTo(
  config: SituationConfig,
  prototype: MindstringPrototype,
  value: boolean,
): SituationConfig {
  return {
    ...config,
    show_date_to: {
      ...config.show_date_to,
      [prototype]: value,
    },
  };
}

export function cloneSituationConfig(config: SituationConfig): SituationConfig {
  return cloneConfig(config);
}

export function buildSituationConfigPatch(
  config: SituationConfig,
  touchedFields: Set<SituationConfigTouchedField>,
): SituationConfigPatch {
  const patch: SituationConfigPatch = {};

  touchedFields.forEach((field) => {
    switch (field) {
      case "show_position":
        patch.show_position = config.show_position;
        break;
      case "mask_position":
        patch.mask_position = config.mask_position;
        break;
      case "override_position":
        patch.override_position = config.override_position;
        break;
      case "position_snippet":
        patch.position_snippet = config.position_snippet;
        break;
      case "show_context":
        patch.show_context = config.show_context;
        break;
      case "mask_context":
        patch.mask_context = config.mask_context;
        break;
      case "override_context":
        patch.override_context = config.override_context;
        break;
      case "show_location":
        patch.show_location = config.show_location;
        break;
      case "override_location":
        patch.override_location = config.override_location;
        break;
      case "show_date_from":
        patch.show_date_from = config.show_date_from;
        break;
      case "show_date_to":
        patch.show_date_to = config.show_date_to;
        break;
    }
  });

  return patch;
}
